import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import './LoginModal.css';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  referralCode?: string;
  isSignup?: boolean;
  onRequireSignup?: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, referralCode, isSignup = false, onRequireSignup }) => {
  const { login, loginWithEmail, isLoading, loginWithProvider } = useAuth();
  const [email, setEmail] = useState('');
  const [inputReferralCode, setInputReferralCode] = useState(referralCode || '');
  const [showLoginOptions, setShowLoginOptions] = useState(!isSignup);
  const [referralError, setReferralError] = useState<string | null>(null);
  const [isReferralValid, setIsReferralValid] = useState(false);

  const validateReferralCode = async (code: string) => {
    if (!code || code.trim().length === 0) {
      setReferralError('Referral code is required');
      setIsReferralValid(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/referral/validate/${code}`);
      const data = await response.json();

      if (!data.valid) {
        setIsReferralValid(false);
        if (data.reason === 'global_cap_reached') {
          setReferralError('We are not accepting any more signups with referral codes for now as we scale our infrastructure. Feel free to signup without one.');
        } else if (data.reason === 'expired') {
          setReferralError('This referral code has expired.');
        } else if (data.reason === 'max_uses_reached') {
          setReferralError('This referral code has reached its maximum uses.');
        } else {
          setReferralError('Invalid referral code.');
        }
      } else {
        setReferralError(null);
        setIsReferralValid(true);
      }
    } catch (error) {
      console.error('Error validating referral code:', error);
      setReferralError(null);
      setIsReferralValid(false);
    }
  };

  // Reset modal state when opened or isSignup changes
  useEffect(() => {
    if (isOpen) {
      setShowLoginOptions(!isSignup);
      setEmail('');
      setReferralError(null);
      setIsReferralValid(false);
      const code = referralCode || '';
      setInputReferralCode(code);
      // Auto-validate if referral code is pre-filled
      if (code && code.trim().length > 0) {
        validateReferralCode(code);
      }
    }
  }, [isOpen, isSignup, referralCode]);

  const handleReferralCodeChange = (value: string) => {
    const upperValue = value.toUpperCase();
    setInputReferralCode(upperValue);
    validateReferralCode(upperValue);
  };

  const handleContinueWithReferral = () => {
    if (isReferralValid && !referralError) {
      setShowLoginOptions(true);
    }
  };

  const handleGoogleLogin = async () => {
    // For signup, validate referral code is present
    if (isSignup) {
      if (!inputReferralCode || !inputReferralCode.trim()) {
        setReferralError('Referral code is required');
        return;
      }

      if (referralError) {
        return;
      }
    }

    try {
      // Backward-compatible: call generic provider flow with 'google'
      await loginWithProvider('google', isSignup ? inputReferralCode : undefined);
      onClose();
    } catch (error) {
      console.error('Google login failed:', error);

      // Check if user needs to signup with referral code
      if ((error as any).requiresReferral && onRequireSignup) {
        onClose();
        setTimeout(() => onRequireSignup(), 300);
      }
    }
  };

  const handleTwitterLogin = async () => {
    if (isSignup) {
      if (!inputReferralCode || !inputReferralCode.trim()) {
        setReferralError('Referral code is required');
        return;
      }
      if (referralError) return;
    }

    try {
      await loginWithProvider('twitter', isSignup ? inputReferralCode : undefined);
      onClose();
    } catch (error) {
      console.error('Twitter login failed:', error);
      if ((error as any).requiresReferral && onRequireSignup) {
        onClose();
        setTimeout(() => onRequireSignup(), 300);
      }
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    // For signup, validate referral code is present
    if (isSignup) {
      if (!inputReferralCode || !inputReferralCode.trim()) {
        setReferralError('Referral code is required');
        return;
      }

      if (referralError) {
        return;
      }
    }

    try {
      await loginWithEmail(email, isSignup ? inputReferralCode : undefined);
      onClose();
    } catch (error) {
      console.error('Email login failed:', error);

      // Check if user needs to signup with referral code
      if ((error as any).requiresReferral && onRequireSignup) {
        onClose();
        setTimeout(() => onRequireSignup(), 300);
      }
    }
  };

  const handleGithubLogin = async () => {
    if (isSignup) {
      if (!inputReferralCode || !inputReferralCode.trim()) {
        setReferralError('Referral code is required');
        return;
      }
      if (referralError) return;
    }

    try {
      await loginWithProvider('github', isSignup ? inputReferralCode : undefined);
      onClose();
    } catch (error) {
      console.error('GitHub login failed:', error);
      if ((error as any).requiresReferral && onRequireSignup) {
        onClose();
        setTimeout(() => onRequireSignup(), 300);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="login-modal-overlay" onClick={onClose}>
      <div className="login-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          ×
        </button>

        <div className="modal-body">
          <h2 className="modal-title">Welcome to TPC</h2>

          {!showLoginOptions ? (
            /* Stage 1: Referral Code Input */
            <div className="referral-section">
              <div className="referral-input-section email-form">
                <input
                  type="text"
                  placeholder="Enter referral code"
                  value={inputReferralCode}
                  onChange={(e) => handleReferralCodeChange(e.target.value)}
                  className="email-input referral-input"
                  maxLength={12}
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  className="email-login-button"
                  onClick={handleContinueWithReferral}
                  disabled={!isReferralValid || !!referralError || isLoading}
                >
                  Continue
                </button>
              </div>
              {referralError && (
                <div className="referral-error-message">
                  {referralError}
                </div>
              )}
            </div>
          ) : (
            /* Stage 2: Login Options */
            <>
              <button
                className="google-login-button"
                onClick={handleGoogleLogin}
                disabled={isLoading}
              >
                <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20" fill="white">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>

              <div className="login-divider">
                <div className="divider-line"></div>
                <span className="divider-text">OR</span>
                <div className="divider-line"></div>
              </div>

              <form onSubmit={handleEmailLogin} className="email-form">
                <input
                  type="email"
                  placeholder="Enter Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="email-input"
                  disabled={isLoading}
                  required
                />
                <button
                  type="submit"
                  className="email-login-button"
                  disabled={isLoading || !email.trim()}
                >
                  {isLoading ? 'Sending...' : 'Continue'}
                </button>
              </form>

              {/* Provider icon grid below email - center aligned. X for now; Github to be added later */}
              <div className="provider-grid" aria-label="Additional sign-in options">
                <button
                  type="button"
                  className="provider-tile"
                  onClick={handleTwitterLogin}
                  disabled={isLoading}
                  title="Continue with X (Twitter)"
                  aria-label="Continue with X (Twitter)"
                >
                  {/* Official X (Twitter) icon */}
                  <svg className="provider-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path fill="#111" d="M14.095479,10.316482L22.286354,1h-1.940718l-7.115352,8.087682L7.551414,1H1l8.589488,12.231093L1,23h1.940717  l7.509372-8.542861L16.448587,23H23L14.095479,10.316482z M11.436522,13.338465l-0.871624-1.218704l-6.924311-9.68815h2.981339  l5.58978,7.82155l0.867949,1.218704l7.26506,10.166271h-2.981339L11.436522,13.338465z"/>
                  </svg>
                </button>

                <button
                  type="button"
                  className="provider-tile"
                  onClick={handleGithubLogin}
                  disabled={isLoading}
                  title="Continue with GitHub"
                  aria-label="Continue with GitHub"
                >
                  {/* GitHub icon */}
                  <svg className="provider-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 512 512" aria-hidden="true" focusable="false">
                    <path fill="#111" d="M256,32C132.3,32,32,134.8,32,261.7c0,101.5,64.2,187.5,153.2,217.9c11.2,2.1,15.3-5,15.3-11.1   c0-5.5-0.2-19.9-0.3-39.1c-62.3,13.9-75.5-30.8-75.5-30.8c-10.2-26.5-24.9-33.6-24.9-33.6c-20.3-14.3,1.5-14,1.5-14   c22.5,1.6,34.3,23.7,34.3,23.7c20,35.1,52.4,25,65.2,19.1c2-14.8,7.8-25,14.2-30.7c-49.7-5.8-102-25.5-102-113.5   c0-25.1,8.7-45.6,23-61.6c-2.3-5.8-10-29.2,2.2-60.8c0,0,18.8-6.2,61.6,23.5c17.9-5.1,37-7.6,56.1-7.7c19,0.1,38.2,2.6,56.1,7.7   c42.8-29.7,61.5-23.5,61.5-23.5c12.2,31.6,4.5,55,2.2,60.8c14.3,16.1,23,36.6,23,61.6c0,88.2-52.4,107.6-102.3,113.3   c8,7.1,15.2,21.1,15.2,42.5c0,30.7-0.3,55.5-0.3,63c0,6.1,4,13.3,15.4,11C415.9,449.1,480,363.1,480,261.7   C480,134.8,379.7,32,256,32z"/>
                  </svg>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginModal;